import { ReactElement, useEffect, useState } from 'react';
import { Button, Card, Col, Form, Input, InputNumber, Row, Select, Space, Spin, Table, TableColumnsType } from 'antd';
import { useTranslation } from 'react-i18next';
import { ApplicationType, EndingType, FeeBasis, GetFeeUnit, GetTrailFeeDto, PropertyPurpose } from '@pmp/api';
import styles from '../styles.module.css';
import { usePmpFetch } from '@pmp/hooks';

const formatGbp = (n: number) => '£' + n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const calculateFeeBasedOnLoanAmount = (loanAmount: number, feeValue: number) => {
  return loanAmount * feeValue;
};

const sameId = (a: string | undefined | null, b: string | undefined | null) =>
  !!a && !!b && String(a).toLowerCase() === String(b).toLowerCase();

export const ProcurationFeeCalculator = (): ReactElement => {
  const [isFetchingFirms, , data, , fetchData] = usePmpFetch('lenderFirmsGetAllLenderFirms');
  const [isFetchingPaymentsRoutes, , firmById, , fetchFirmById] = usePmpFetch('lenderFirmsGetLenderFirm');
  const [isCalculating, , calcResult, , calculate, clearCalculation] = usePmpFetch('procurationFeeCalculatorCalculate');
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(false);
  const [form] = Form.useForm();
  const selectedFirmId = Form.useWatch('firm', form);
  const loanAmount = Form.useWatch('loan', form);

  useEffect(() => {
    fetchData();
  }, [fetchData]);


  useEffect(() => {
    if (selectedFirmId) {
      fetchFirmById({ path: { firmId: selectedFirmId } });
    }
  }, [selectedFirmId, fetchFirmById]);

  const firms = data ?? [];

  const ownerFirmOptions = firms.map(firm => ({ label: firm.name, value: firm.id }));

  const submissionRouteIds = selectedFirmId
    ? [
        ...new Set(
          [firmById?.payments?.defaultPaymentRouteFirmId, ...(firmById?.payments?.paymentRouteFirmIds ?? [])].filter(Boolean) as string[],
        ),
      ]
    : [];
  const submissionRouteOptions = submissionRouteIds
    .map(id => firms.find(f => sameId(f.id, id)))
    .filter(Boolean)
    .map(firm => ({ label: firm!.name, value: firm!.ohpenId }));

  const packagerFirmOptions = (firmById?.packaging?.packagerFirmIds ?? [])
    .map(id => firms.find(f => sameId(f.id, id)))
    .filter(Boolean)
    .map(firm => ({ label: firm!.name, value: firm!.id }));

  const applicationTypeOptions = Object.values(ApplicationType).map(v => ({
    label: t(`lender.procurationFees.labels.applicationTypeOptions.${v}`),
    value: v,
  }));

  const propertyPurposeOptions = Object.values(PropertyPurpose).map(v => ({
    label: t(`lender.procurationFees.labels.propertyPurposeOptions.${v}`),
    value: v,
  }));

  const routePlaceholderKey = !selectedFirmId
    ? 'paymentRouteNoFirmPlaceholder'
    : submissionRouteOptions.length
      ? 'paymentRoutePlaceholder'
      : 'paymentRouteNoRoutesPlaceholder';

  const packagerPlaceholderKey = !selectedFirmId
    ? 'packagerFirmNoFirmPlaceholder'
    : packagerFirmOptions.length
      ? 'packagerFirmPlaceholder'
      : 'packagerFirmNoPackagersPlaceholder';

  const pipelineSteps = [
    {
      key: 'firm',
      label: t('lender.procurationFees.labels.ownerFirm'),
      placeholder: t('lender.procurationFees.labels.ownerFirmPlaceholder'),
      required: true,
      options: ownerFirmOptions,
    },
    {
      key: 'submissionRoute',
      required: true,
      label: t('lender.procurationFees.labels.paymentRoute'),
      placeholder: t(`lender.procurationFees.labels.${routePlaceholderKey}`),
      options: submissionRouteOptions,
      isDisabled: !selectedFirmId,
    },
    {
      key: 'scope',
      fields: [
        {
          key: 'applicationType',
          label: t('lender.procurationFees.labels.applicationType'),
          options: applicationTypeOptions as { label: string; value: string }[],
          required: true,
        },
        {
          key: 'propertyPurpose',
          label: t('lender.procurationFees.labels.propertyPurpose'),
          options: propertyPurposeOptions as { label: string; value: string }[],
        },
      ],
    },
    {
      key: 'packager',
      label: t('lender.procurationFees.labels.packagerFirm'),
      placeholder: t(`lender.procurationFees.labels.${packagerPlaceholderKey}`),
      options: packagerFirmOptions,
    },
  ];

  const handleCalculate = () => {
    form
      .validateFields()
      .then(values => {
        clearCalculation();
        calculate({
          body: {
            ownerFirmId: values.firm,
            submissionRouteId: values.submissionRoute,
            packagerFirmId: values.packager,
            ...(values.applicationType !== undefined && { applicationType: values.applicationType }),
            ...(values.propertyPurpose !== undefined && { propertyPurpose: values.propertyPurpose }),
            ...(values.loan ? { loanParts: [{ loanAmount: values.loan }] } : {}),
          },
        });
        setSubmitted(true);
      })
      .catch(() => undefined);
  };

  const result = submitted ? calcResult : null;

  const finalFee = (value: number | undefined, unit: GetFeeUnit | undefined): string => {
    if (value === undefined) return '—';
    return unit === GetFeeUnit.CURRENCY ? formatGbp(value) : `${(value / 100)}%`;
  };

  const signedFee = (value: number | undefined, unit: GetFeeUnit | undefined): string => {
    if (value == null) return '—';
    return `${value < 0 ? '- ' : '+ '}${finalFee(Math.abs(value), unit)}`;
  };

  const dash = (v: number | undefined) => (v == null ? '—' : v);

  const feeSummary = (): string => {
    if (!result) return t('lender.procurationFees.labels.chooseOptions');
    if (!result.isDefault) return result.feeName ?? '';
    return result.feeName
      ? t('lender.procurationFees.labels.isDefaultNamed', { feeName: result.feeName })
      : t('lender.procurationFees.labels.isDefault');
  };

  const firmBoosts = (result?.firmBoosts ?? []).map(boost => ({
    ...boost,
    firmName: firms.find(firm => sameId(firm.id, boost.firmId))?.name ?? boost.firmId,
  }));

  const feeToGbp = (loan: number, value: number | undefined, unit: GetFeeUnit): number =>
    unit === GetFeeUnit.BASIS_POINTS ? calculateFeeBasedOnLoanAmount(loan, (value ?? 0) / 10000) : (value ?? 0);

  const completionFeeGbp = (loan: number): number =>
    feeToGbp(loan, result?.completionFee?.value, result?.completionFee?.unit ?? GetFeeUnit.CURRENCY);

  const trailFeeColumns: TableColumnsType<GetTrailFeeDto> = [
    {
      title: t('lender.procurationFees.labels.value'),
      dataIndex: 'value',
      render: (_, row) => finalFee(row.value, row.unit),
    },
    {
      title: t('lender.procurationFees.labels.basis'),
      dataIndex: 'basis',
      render: (basis: FeeBasis | null | undefined) => (basis ? t(`lender.procurationFees.labels.basisOptions.${basis}`) : '—'),
    },
    {
      title: t('lender.procurationFees.labels.startingMonth'),
      dataIndex: 'startingMonth',
      render: dash,
    },
    {
      title: t('lender.procurationFees.labels.periodInMonths'),
      dataIndex: 'intervalInMonths',
      render: dash,
    },
    {
      title: t('lender.procurationFees.labels.endingType'),
      dataIndex: 'endingType',
      render: (endingType: EndingType) => t(`lender.procurationFees.labels.endingTypeOptions.${endingType}`),
    },
  ];

  const isLoading = isFetchingFirms || isFetchingPaymentsRoutes || isCalculating;

  return (
    <Card styles={{ body: { padding: 0 } }} className={styles.calculator}>
      <Spin spinning={isLoading}>
        <Form
          form={form}
          layout="vertical"
          onValuesChange={changed => {
            setSubmitted(false);
            if ('firm' in changed) {
              form.setFieldValue('submissionRoute', undefined);
              form.setFieldValue('packager', undefined);
            }
          }}
        >
          <section className={styles.scope}>
            <div className={styles.scopeField}>
              <Form.Item label={<>{t('lender.procurationFees.labels.loanAmount')}</>} style={{ marginBottom: 0 }}>
                <Space.Compact>
                  <Input className={styles.currencyPrefix} defaultValue="£" disabled />
                  <Form.Item name="loan" initialValue={null} noStyle>
                    <InputNumber<number>
                      formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={v => Number(v?.replace(/,/g, '') || 0)}
                      min={1}
                      precision={0}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Space.Compact>
              </Form.Item>
            </div>
          </section>

          <div className={styles.sectionRule} />

          <section className={styles.pipeline}>
            {pipelineSteps.map((step, i) => {
              const isFirst = i === 0;
              const isLast = i === pipelineSteps.length - 1;
              return (
                <div key={step.key} className={styles.step}>
                  <div className={styles.rail}>
                    <div
                      className={[styles.railLine, isFirst && styles.railLineFirst, isLast && styles.railLineLast]
                        .filter(Boolean)
                        .join(' ')}
                    />
                    <div className={styles.node}>{i + 1}</div>
                  </div>
                  {'fields' in step && step.fields != null ? (
                    <div className={styles.stepBodyRow}>
                      {step.fields.map(field => (
                        <Form.Item
                          key={field.key}
                          name={field.key}
                          label={<>{field.label}</>}
                          style={{ marginBottom: 0, flex: 1 }}
                          rules={field.required ? [{ required: true, message: t('global.errors.required') }] : []}
                        >
                          <Select options={field.options} allowClear style={{ width: '100%' }} />
                        </Form.Item>
                      ))}
                    </div>
                  ) : (
                    <div className={styles.stepBody}>
                      <Form.Item
                        name={step.key}
                        label={<>{step.label}</>}
                        style={{ marginBottom: 0 }}
                        rules={step.required ? [{ required: true, message: t('global.errors.required') }] : []}
                      >
                        <Select
                          options={step.options}
                          placeholder={step.placeholder}
                          allowClear
                          showSearch
                          disabled={step.isDisabled || !step.options.length}
                          optionFilterProp="label"
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    </div>
                  )}
                </div>
              );
            })}
          </section>
        </Form>

        <section className={styles.total}>
          <Row justify={'space-between'}>
            <Col>
              <span className={styles.totalLabel}>{t('lender.procurationFees.labels.completionFee')}</span>
              <div className={styles.totalSub}>{feeSummary()}</div>
            </Col>
            <Col>
              <span className={styles.totalValue}>
                {result ? finalFee(result.completionFee?.value, result.completionFee?.unit) : '0.00'}
              </span>
            </Col>
          </Row>
          {loanAmount &&
            result?.completionFee?.value != null &&
            result.completionFee.unit === GetFeeUnit.BASIS_POINTS && (
              <Row justify={'space-between'} style={{ marginTop: '1rem' }}>
                <Col>
                  <span className={styles.totalLabel}>{t('lender.procurationFees.labels.completionFeeBasedOnLoanAmount')}</span>
                </Col>
                <Col>
                  <span className={styles.totalValue}>{formatGbp(completionFeeGbp(loanAmount))}</span>
                </Col>
              </Row>
            )}
          {!!firmBoosts.length && (
            <>
              <Row className={styles.boosts}>
                <Col>
                  <span className={styles.totalLabel}>{t('lender.procurationFees.labels.firmBoosts')}</span>
                </Col>
              </Row>
              {firmBoosts.map((boost, index) => (
                <Row key={`${boost.firmId}-${index}`} justify={'space-between'} className={styles.boostRow}>
                  <Col>
                    <span className={styles.totalSub}>{boost.firmName}</span>
                  </Col>
                  <Col>
                    <span className={styles.totalSub}>{signedFee(boost.value, boost.unit)}</span>
                  </Col>
                </Row>
              ))}
            </>
          )}
          {!!result?.trailFees?.length && (
            <>
              <Row className={styles.trailFee}>
                <Col>
                  <span className={styles.totalLabel}>
                    {t('lender.procurationFees.labels.trailFee')}
                  </span>
                </Col>
              </Row>
              <Table<GetTrailFeeDto>
                className={styles.trailTable}
                size="small"
                pagination={false}
                columns={trailFeeColumns}
                dataSource={result.trailFees}
                rowHoverable={false}
                rowKey={(_, index) => String(index)}
              />
            </>
          )}
        </section>
        <Button className={styles.calcBtn} type="primary" onClick={handleCalculate}>
          {result ? t('lender.procurationFees.buttons.recalculate') : t('lender.procurationFees.buttons.calculate')}
        </Button>
      </Spin>
    </Card>
  );
};
